/* eslint-disable max-lines */
/* eslint-disable max-lines-per-function */
import { Button, Icon } from '@equinor/eds-core-react';
import { expand as EXPAND } from '@equinor/eds-icons';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ComputeCaseDto,
  ComputeMethod,
  InputValueType,
  ListComputeSettingsModelDto,
  ModelAreaDto,
} from '../../../../../api/generated';
import { CaseSettingSelect } from '../CaseSettingSelects/CaseSettingSelect';
import { ModelAreaSelect } from '../CaseSettingSelects/ModelAreaSelect';
import * as Styled from '../CaseSettingSelects/SettingSelect.styled';
import { ViewSelectedVariogramSettings } from '../ViewSelectedVariogramSettings/ViewSelectedVariogramSettings';
import { Variants } from '@equinor/eds-core-react/dist/types/components/types';
import { useCaseRowStore } from '../../../../../stores/CaseRowStore';
import {
  isIndicatorOptionExhausted,
  isNetToGrossOptionExhausted,
  isSetBasedVariogramOptionExhausted,
  isVariogramModelAreaExhausted,
} from '../../../utils/variogramCaseUtils';

export const VariogramOptionSelect = ({
  rowCase,
  modelAreas,
  caseType,
  selectedModelArea,
  selectedIndicatorParameters,
  selectedGrainSize,
  selectedContiniousParameters,
  selectedArchelFilter,
  selectedVariogramModels,
  setModelArea,
  setIndicatorParameters,
  setGrainSize,
  setContiniousParameters,
  setArchelFilter,
  setVariogramModels,
  NetGrossGrainSizeSettings,
  NetGrossVariogramFamilySettings,
  NetGrossArchelSettings,
  contParamsVariogramFamilySettings,
  contParamsParamsSettings,
  contParamsArchelSettings,
  indicatorFamilySettings,
  indicatorIndicatorSettings,
  existingCases,
  saved,
  isEditing,
  setHasChanges,
  selectedParamValue,
}: {
  rowCase: ComputeCaseDto;
  modelAreas: ModelAreaDto[];
  caseType: ComputeMethod;
  selectedModelArea?: ModelAreaDto[] | undefined;
  selectedIndicatorParameters?: ListComputeSettingsModelDto[];
  selectedGrainSize?: ListComputeSettingsModelDto[];
  selectedContiniousParameters?: ListComputeSettingsModelDto[];
  selectedArchelFilter?: ListComputeSettingsModelDto[] | undefined;
  selectedVariogramModels: ListComputeSettingsModelDto[] | undefined;

  setModelArea?: React.Dispatch<
    React.SetStateAction<ModelAreaDto[] | undefined>
  >;
  setIndicatorParameters?: React.Dispatch<
    React.SetStateAction<ListComputeSettingsModelDto[] | undefined>
  >;
  setGrainSize?: React.Dispatch<
    React.SetStateAction<ListComputeSettingsModelDto[] | undefined>
  >;
  setContiniousParameters?: React.Dispatch<
    React.SetStateAction<ListComputeSettingsModelDto[] | undefined>
  >;
  setArchelFilter?: React.Dispatch<
    React.SetStateAction<ListComputeSettingsModelDto[] | undefined>
  >;
  setVariogramModels: React.Dispatch<
    React.SetStateAction<ListComputeSettingsModelDto[] | undefined>
  >;
  indicatorFamilySettings?: ListComputeSettingsModelDto[] | undefined;
  indicatorIndicatorSettings?: ListComputeSettingsModelDto[] | undefined;
  NetGrossGrainSizeSettings?: ListComputeSettingsModelDto[] | undefined;
  NetGrossVariogramFamilySettings?: ListComputeSettingsModelDto[] | undefined;
  NetGrossArchelSettings?: ListComputeSettingsModelDto[] | undefined;
  contParamsVariogramFamilySettings?: ListComputeSettingsModelDto[] | undefined;
  contParamsParamsSettings?: ListComputeSettingsModelDto[] | undefined;
  contParamsArchelSettings?: ListComputeSettingsModelDto[] | undefined;
  existingCases: ComputeCaseDto[];
  saved: boolean;
  isEditing: boolean;
  setHasChanges: (value: boolean) => void;
  selectedParamValue: (method: string) => ListComputeSettingsModelDto[];
}) => {
  const [expandSettings, setExpandSettings] = useState<boolean>(false);
  const grainSelectedByUser = useRef(false);
  const modelAreaSelectedByUser = useRef(false);
  const {
    indicatorParams,
    indicatorVariogramModel,
    netToGrossGrain,
    netToGrossVariogramModel,
    contParamParameters,
    contParamVariogramModel,
    indicatorModelArea,
    netToGrossModelArea,
    contParamModelArea,
  } = useCaseRowStore();

  const getDefaultParameters = (
    loadedParameters: ListComputeSettingsModelDto[],
    selectedParameter: ListComputeSettingsModelDto[] | undefined,
  ) => {
    let list: ListComputeSettingsModelDto[] = [];

    if (loadedParameters !== undefined && selectedParameter === undefined) {
      list = loadedParameters;
    } else if (
      selectedParameter !== undefined &&
      loadedParameters === undefined
    ) {
      list = selectedParameter;
    } else if (
      selectedParameter !== undefined &&
      loadedParameters !== undefined
    ) {
      list = selectedParameter;
    }
    return list;
  };

  const setIfLoadedValues = (inputValueType: InputValueType) => {
    const loaded = selectedParamValue(inputValueType);
    switch (inputValueType) {
      case InputValueType.INDICATOR:
        if (loaded)
          return getDefaultParameters(loaded, selectedIndicatorParameters);
        break;

      case InputValueType.VARIOGRAM_FAMILY_FILTER:
        return getDefaultParameters(loaded, selectedVariogramModels);

      case InputValueType.NET_TO_GROSS:
        if (loaded) return getDefaultParameters(loaded, selectedGrainSize);
        break;

      case InputValueType.ATTRIBUTE_NAME:
        if (loaded)
          return getDefaultParameters(loaded, selectedContiniousParameters);
        break;

      case InputValueType.ARCHEL:
        if (loaded) return getDefaultParameters(loaded, selectedArchelFilter);

        break;
    }
  };

  const handleExpandView = () => {
    setExpandSettings(!expandSettings);
  };

  const readOnly = saved && !isEditing;

  const parameterOptions = useMemo(
    () =>
      caseType === ComputeMethod.INDICATOR
        ? indicatorIndicatorSettings ?? []
        : caseType === ComputeMethod.NET_TO_GROSS
        ? NetGrossGrainSizeSettings ?? []
        : contParamsParamsSettings ?? [],
    [
      NetGrossGrainSizeSettings,
      caseType,
      contParamsParamsSettings,
      indicatorIndicatorSettings,
    ],
  );

  const additionalParameterOptions = useMemo(
    () =>
      caseType === ComputeMethod.NET_TO_GROSS
        ? NetGrossArchelSettings ?? []
        : caseType === ComputeMethod.CONTINIOUS_PARAMETER
        ? contParamsArchelSettings ?? []
        : [],
    [NetGrossArchelSettings, caseType, contParamsArchelSettings],
  );

  const modelOptions = useMemo(
    () =>
      caseType === ComputeMethod.INDICATOR
        ? indicatorFamilySettings ?? []
        : caseType === ComputeMethod.NET_TO_GROSS
        ? NetGrossVariogramFamilySettings ?? []
        : contParamsVariogramFamilySettings ?? [],
    [
      NetGrossVariogramFamilySettings,
      caseType,
      contParamsVariogramFamilySettings,
      indicatorFamilySettings,
    ],
  );

  const isModelAreaDisabled = useCallback(
    (option: ModelAreaDto) =>
      isVariogramModelAreaExhausted(
        existingCases,
        rowCase.computeCaseId,
        caseType,
        option,
        parameterOptions,
        modelOptions,
        additionalParameterOptions,
      ),
    [
      additionalParameterOptions,
      caseType,
      existingCases,
      modelOptions,
      parameterOptions,
      rowCase.computeCaseId,
    ],
  );

  const isParameterDisabled = useCallback(
    (option: ListComputeSettingsModelDto) =>
      caseType === ComputeMethod.NET_TO_GROSS
        ? isNetToGrossOptionExhausted(
            existingCases,
            rowCase.computeCaseId,
            selectedModelArea,
            selectedGrainSize?.[0],
            selectedArchelFilter ?? [],
            selectedVariogramModels ?? [],
            option,
            modelOptions,
            additionalParameterOptions,
          )
        : caseType === ComputeMethod.CONTINIOUS_PARAMETER
        ? isSetBasedVariogramOptionExhausted(
            existingCases,
            rowCase.computeCaseId,
            caseType,
            selectedModelArea,
            selectedContiniousParameters ?? [],
            selectedArchelFilter ?? [],
            selectedVariogramModels ?? [],
            option,
            modelOptions,
            additionalParameterOptions,
          )
        : isIndicatorOptionExhausted(
            existingCases,
            rowCase.computeCaseId,
            selectedModelArea,
            selectedIndicatorParameters ?? [],
            selectedVariogramModels ?? [],
            option,
            parameterOptions,
            modelOptions,
          ),
    [
      additionalParameterOptions,
      caseType,
      existingCases,
      modelOptions,
      parameterOptions,
      rowCase.computeCaseId,
      selectedArchelFilter,
      selectedContiniousParameters,
      selectedGrainSize,
      selectedIndicatorParameters,
      selectedModelArea,
      selectedVariogramModels,
    ],
  );

  useEffect(() => {
    if (
      saved ||
      modelAreaSelectedByUser.current ||
      !setModelArea ||
      modelAreas.length === 0
    )
      return;

    const selectedArea = selectedModelArea?.[0];
    const selectedAreaIsAvailable =
      selectedArea && !isModelAreaDisabled(selectedArea);
    if (selectedAreaIsAvailable) return;

    const firstAvailableArea = modelAreas.find(
      (option) => !isModelAreaDisabled(option),
    );
    if (firstAvailableArea) setModelArea([firstAvailableArea]);
  }, [isModelAreaDisabled, modelAreas, saved, selectedModelArea, setModelArea]);

  useEffect(() => {
    if (
      caseType !== ComputeMethod.NET_TO_GROSS ||
      saved ||
      grainSelectedByUser.current ||
      !setGrainSize ||
      parameterOptions.length === 0
    )
      return;

    const selectedGrain = selectedGrainSize?.[0];
    const selectedGrainIsAvailable =
      selectedGrain && !isParameterDisabled(selectedGrain);
    if (selectedGrainIsAvailable) return;

    const firstAvailableGrain = parameterOptions.find(
      (option) => !isParameterDisabled(option),
    );
    if (firstAvailableGrain) setGrainSize([firstAvailableGrain]);
  }, [
    caseType,
    existingCases,
    parameterOptions,
    additionalParameterOptions,
    modelOptions,
    saved,
    selectedArchelFilter,
    selectedGrainSize,
    selectedModelArea,
    selectedVariogramModels,
    setGrainSize,
    isParameterDisabled,
  ]);

  const wrapSetter = <T,>(
    setter: React.Dispatch<React.SetStateAction<T>> | undefined,
  ): React.Dispatch<React.SetStateAction<T>> | undefined => {
    if (!setter) return undefined;
    return (value: React.SetStateAction<T>) => {
      setter(value);
      setHasChanges(true);
    };
  };

  const setGrainByUser: React.Dispatch<
    React.SetStateAction<ListComputeSettingsModelDto[] | undefined>
  > = (value) => {
    grainSelectedByUser.current = true;
    setGrainSize?.(value);
    setHasChanges(true);
  };

  const setModelAreaByUser: React.Dispatch<
    React.SetStateAction<ModelAreaDto[] | undefined>
  > = (value) => {
    modelAreaSelectedByUser.current = true;
    setModelArea?.(value);
    setHasChanges(true);
  };

  const modelAreaVariant = (): Variants | undefined => {
    if (
      caseType === ComputeMethod.INDICATOR &&
      selectedModelArea?.filter((c) => c.modelAreaType !== '').length === 0 &&
      indicatorModelArea
    ) {
      return 'error';
    }
    if (
      caseType === ComputeMethod.NET_TO_GROSS &&
      selectedModelArea?.filter((c) => c.modelAreaType !== '').length === 0 &&
      netToGrossModelArea
    ) {
      return 'error';
    }
    if (
      caseType === ComputeMethod.CONTINIOUS_PARAMETER &&
      selectedModelArea?.filter((c) => c.modelAreaType !== '').length === 0 &&
      contParamModelArea
    ) {
      return 'error';
    }
    return undefined;
  };

  const parameterVariant = (): Variants | undefined => {
    if (
      caseType === ComputeMethod.INDICATOR &&
      selectedIndicatorParameters?.filter(
        (c) => c.inputValueType === InputValueType.INDICATOR,
      ).length === 0 &&
      indicatorParams
    )
      return 'error';

    if (
      caseType === ComputeMethod.NET_TO_GROSS &&
      selectedGrainSize?.filter(
        (c) => c.inputValueType === InputValueType.NET_TO_GROSS,
      ).length === 0 &&
      netToGrossGrain
    )
      return 'error';

    if (
      caseType === ComputeMethod.CONTINIOUS_PARAMETER &&
      selectedContiniousParameters?.filter(
        (c) => c.inputValueType === InputValueType.ATTRIBUTE_NAME,
      ).length === 0 &&
      contParamParameters
    )
      return 'error';

    return undefined;
  };

  const variogramModelVariant = (): Variants | undefined => {
    if (
      caseType === ComputeMethod.INDICATOR &&
      selectedVariogramModels?.filter((c) => c.computeSettingId !== '')
        .length === 0 &&
      indicatorVariogramModel
    ) {
      return 'error';
    }
    if (
      caseType === ComputeMethod.NET_TO_GROSS &&
      selectedVariogramModels?.filter((c) => c.computeSettingId !== '')
        .length === 0 &&
      netToGrossVariogramModel
    ) {
      return 'error';
    }
    if (
      caseType === ComputeMethod.CONTINIOUS_PARAMETER &&
      selectedVariogramModels?.filter((c) => c.computeSettingId !== '')
        .length === 0 &&
      contParamVariogramModel
    ) {
      return 'error';
    }
    return undefined;
  };

  return (
    <Styled.AutocompleteWrapper>
      <Styled.ButtonWrapper>
        <Button
          variant="ghost_icon"
          aria-label="expand"
          onClick={handleExpandView}
        >
          <Icon
            data={EXPAND}
            size={24}
            style={{
              transform: expandSettings ? 'rotate(270deg)' : 'rotate(90deg)',
              transition: 'transform 0.3s ease',
            }}
          ></Icon>
        </Button>
      </Styled.ButtonWrapper>
      <ViewSelectedVariogramSettings
        expandSettings={expandSettings}
        selectedModelArea={selectedModelArea}
      >
        <ModelAreaSelect
          disableSelect={readOnly}
          modelAreas={modelAreas}
          selectedModelArea={selectedModelArea}
          setModelArea={setModelArea ? setModelAreaByUser : undefined}
          existingCases={existingCases}
          caseType="Variogram"
          variant={modelAreaVariant()}
          customOptionDisabled={isModelAreaDisabled}
        />
      </ViewSelectedVariogramSettings>

      {setIndicatorParameters && caseType === ComputeMethod.INDICATOR && (
        <ViewSelectedVariogramSettings
          expandSettings={expandSettings}
          selecteSettings={
            setIfLoadedValues && setIfLoadedValues(InputValueType.INDICATOR)
          }
        >
          <CaseSettingSelect
            label={'Parameter'}
            options={indicatorIndicatorSettings}
            caseMethod={rowCase.computeMethod}
            settingType={InputValueType.INDICATOR}
            selectedValue={
              setIfLoadedValues && setIfLoadedValues(InputValueType.INDICATOR)
            }
            setValue={wrapSetter(setIndicatorParameters)}
            variant={parameterVariant()}
            readOnly={readOnly}
            customOptionDisabled={isParameterDisabled}
          />
        </ViewSelectedVariogramSettings>
      )}

      {setGrainSize && caseType === ComputeMethod.NET_TO_GROSS && (
        <ViewSelectedVariogramSettings
          expandSettings={expandSettings}
          selecteSettings={
            setIfLoadedValues && setIfLoadedValues(InputValueType.NET_TO_GROSS)
          }
        >
          <CaseSettingSelect
            label={'From grain size'}
            settingType={InputValueType.NET_TO_GROSS}
            caseMethod={rowCase.computeMethod}
            options={NetGrossGrainSizeSettings}
            selectedValue={
              setIfLoadedValues &&
              setIfLoadedValues(InputValueType.NET_TO_GROSS)
            }
            setValue={setGrainByUser}
            variant={parameterVariant()}
            readOnly={readOnly}
            customOptionDisabled={isParameterDisabled}
          />
        </ViewSelectedVariogramSettings>
      )}
      {setContiniousParameters &&
        caseType === ComputeMethod.CONTINIOUS_PARAMETER && (
          <ViewSelectedVariogramSettings
            expandSettings={expandSettings}
            selecteSettings={
              setIfLoadedValues &&
              setIfLoadedValues(InputValueType.ATTRIBUTE_NAME)
            }
          >
            <CaseSettingSelect
              label={'Parameter'}
              settingType={InputValueType.ATTRIBUTE_NAME}
              caseMethod={rowCase.computeMethod}
              options={contParamsParamsSettings}
              selectedValue={
                setIfLoadedValues &&
                setIfLoadedValues(InputValueType.ATTRIBUTE_NAME)
              }
              setValue={wrapSetter(setContiniousParameters)}
              variant={parameterVariant()}
              readOnly={readOnly}
              customOptionDisabled={isParameterDisabled}
            />
          </ViewSelectedVariogramSettings>
        )}
      <ViewSelectedVariogramSettings
        expandSettings={expandSettings}
        selecteSettings={
          setIfLoadedValues && setIfLoadedValues(InputValueType.ARCHEL)
        }
      >
        <CaseSettingSelect
          label="Architectural elements filter"
          settingType={InputValueType.ARCHEL}
          caseMethod={rowCase.computeMethod}
          options={
            caseType === ComputeMethod.NET_TO_GROSS
              ? NetGrossArchelSettings
              : contParamsArchelSettings
          }
          selectedValue={
            setIfLoadedValues && setIfLoadedValues(InputValueType.ARCHEL)
          }
          setValue={wrapSetter(setArchelFilter)}
          readOnly={readOnly}
          customOptionDisabled={isParameterDisabled}
        />
      </ViewSelectedVariogramSettings>

      <ViewSelectedVariogramSettings
        expandSettings={expandSettings}
        selecteSettings={
          setIfLoadedValues &&
          setIfLoadedValues(InputValueType.VARIOGRAM_FAMILY_FILTER)
        }
      >
        <CaseSettingSelect
          label={'Variogram model'}
          settingType={InputValueType.VARIOGRAM_FAMILY_FILTER}
          caseMethod={rowCase.computeMethod}
          options={
            caseType === ComputeMethod.NET_TO_GROSS
              ? NetGrossVariogramFamilySettings
              : caseType === ComputeMethod.INDICATOR
              ? indicatorFamilySettings
              : contParamsVariogramFamilySettings
          }
          selectedValue={
            setIfLoadedValues &&
            setIfLoadedValues(InputValueType.VARIOGRAM_FAMILY_FILTER)
          }
          setValue={wrapSetter(setVariogramModels)}
          variant={variogramModelVariant()}
          readOnly={readOnly}
          customOptionDisabled={isParameterDisabled}
        />
      </ViewSelectedVariogramSettings>
    </Styled.AutocompleteWrapper>
  );
};
